import { useEffect } from "react";
import NavBar from "@/components/site/NavBar";
import Hero from "@/components/site/Hero";
import AttorneyBar from "@/components/site/AttorneyBar";
import FearSection from "@/components/site/FearSection";
import FounderStory from "@/components/site/FounderStory";
import GoogleReviews from "@/components/site/GoogleReviews";
import Testimonials from "@/components/site/Testimonials";
import FinalCTA from "@/components/site/FinalCTA";
import Footer from "@/components/site/Footer";
import { useAbRedirect } from "@/lib/useAbRedirect";

const heroImage =
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1200";

const headline = (
  <>
    Getting married in OC?{" "}
    <span className="italic font-medium text-teal">
      Protect what you&rsquo;ve built.
    </span>
  </>
);

const subhead = (
  <>
    A prenuptial agreement isn&rsquo;t about distrust — it&rsquo;s about{" "}
    <span className="text-ink font-medium">clarity</span>.{" "}
    <span className="text-ink font-medium">Binoye Jos, Esq.</span> and the JOS
    Family Law team draft prenups and postnups in Orange County that are{" "}
    <span className="text-ink font-medium">legally binding</span> to protect
    what you&rsquo;ve built, based on what you want to accomplish. Do you
    believe it&rsquo;s worth protecting your assets? If so, contact us.
  </>
);

const pullquote = (
  <>
    Smart couples protect their marriage like their most valuable asset.{" "}
    <span className="italic text-teal">
      Secure your future and focus on love. Let us protect what you build
      together.
    </span>
  </>
);

const statBar = [
  { num: "80+", label: "Prenups Drafted" },
  { num: "100%", label: "Confidential Process" },
  { num: "1:1", label: "Attorney Attention" },
];

const fears = [
  "What happens to my business if we divorce?",
  "How do we protect assets we're each bringing in?",
  "We're already married — is a postnup still an option?",
  "Will a prenup actually hold up in a California court?",
];

const founderHeading = (
  <>
    Binoye Jos, Esq. understands what assets are worth protecting.{" "}
    <span className="italic">Let him help you protect yours.</span>
  </>
);

const founderParagraphs = [
  "Binoye Jos hired two prominent law firms and spent $400,000 on his own custody case. Both told him he'd never get equal custody. He represented himself, won equal custody, then went to law school. He built four companies before becoming a family law attorney — so couples in Orange County could enter marriage with clarity, not fear.",
  "Every prenup and postnup at this firm is drafted to be fair to both parties, legally sound under California law, and quiet about the things that should stay private.",
];

const testimonials = [
  {
    name: "Ms. Valdez",
    quote:
      "This firm is not in the business of telling you what you want to hear. It is focused on what is best for your family. The level of empathy and legal experience is the kind most attorneys cannot match.",
  },
  {
    name: "Mr. Kogami",
    quote:
      "Jos Family Law will fight for your case and what is fair. If it wasn't for them, I would not have been able to come to a settlement I felt was fair for both parties.",
  },
  {
    name: "Ms. Lee",
    quote:
      "Founded by someone who went through this himself, the level of empathy and legal experience is the kind most attorneys cannot match. I trusted them completely.",
  },
];

const closingLine =
  "These aren't pessimistic questions — they're smart ones. A well-drafted prenup protects both of you. That's exactly what we do.";

export default function Prenup() {
  useAbRedirect(
    "/prenuptial-agreement-attorney-orange-county-vsl",
    "/prenuptial-agreement-attorney-orange-county-vsl-v2",
    "prenup"
  );
  useEffect(() => {
    document.title =
      "Jos Family Law — Prenuptial Agreement Attorney, Orange County CA";
    const meta =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.name = "description";
        document.head.appendChild(m);
        return m;
      })();
    meta.setAttribute(
      "content",
      "Getting married in OC? A prenuptial agreement protects both parties clearly and fairly. Founded by Binoye Jos, Esq. — an attorney who built four companies before practicing law. Call (714) 733-7066."
    );
  }, []);

  return (
    <main data-testid="prenup-page" className="relative bg-paper text-ink">
      <NavBar />
      <Hero
        source="Prenup"
        eyebrow="Family Law Attorney · Orange County, California"
        headline={headline}
        subhead={subhead}
        primaryCtaLabel="Schedule Your Consultation"
        ctaSubNote="Confidential · No Obligation"
        statBar={statBar}
        heroImage={heroImage}
        imageCaption="Getting married in OC? One call tells you exactly where you stand."
        pullquoteLabel="Our Promise"
        pullquote={pullquote}
        formCtaLabel="Request a Consultation"
        statHeading="How We Work"
        statContext="Every prenup is drafted with confidentiality and one-on-one attorney attention — no assembly-line templates, no rushed forms."
      />
      <AttorneyBar />
      <FearSection
        eyebrow="The Smart Questions"
        heading={
          <>
            The questions worth asking{" "}
            <span className="italic font-medium">before you say I do.</span>
          </>
        }
        fears={fears}
        closingLine={closingLine}
      />
      <FounderStory heading={founderHeading} paragraphs={founderParagraphs} />
      <GoogleReviews />
      <Testimonials
        items={testimonials}
        heading={
          <>
            Satisfied <span className="italic">clients.</span>
          </>
        }
        clientLabel="Family Law Client"
      />
      <FinalCTA source="Prenup" formCtaLabel="Schedule Your Consultation" />
      <Footer />
    </main>
  );
}
